import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileText, Landmark, Scale, ArrowRight } from 'lucide-react';
import cesarPhoto from '../assets/funcionarios/cesar_fuentes.jpg';
import comunicadoService from '../services/comunicadoService';

export default function HomenajeModal({ isPreview = false, datosPreview = null, onClose = null }) {
  const [isOpen, setIsOpen] = useState(isPreview);
  const [comunicado, setComunicado] = useState(datosPreview);
  const navigate = useNavigate();

  useEffect(() => {
    if (isPreview) {
      setIsOpen(true);
      setComunicado(datosPreview);
      return;
    }

    // Modal público: cargar configuración desde la API
    const cargarComunicado = async () => {
      try {
        const hasSeenModal = sessionStorage.getItem('cbrComunicadoVisto');
        if (hasSeenModal) return;

        const data = await comunicadoService.get();
        if (data && data.activo) {
          setComunicado(data);
          setIsOpen(true);
        }
      } catch (err) {
        console.warn('No se pudo cargar el comunicado modal dinámico:', err);
      }
    };

    cargarComunicado();
  }, [isPreview, datosPreview]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleCerrar = () => {
    setIsOpen(false);
    if (!isPreview) {
      sessionStorage.setItem('cbrComunicadoVisto', 'true');
    }
    if (onClose) onClose();
  };

  const handleBotonPrincipal = () => {
    handleCerrar();
    const url = comunicado?.boton_url;
    if (url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    }
  };

  if (!isOpen || !comunicado) return null;

  const renderIcono = () => {
    const tipo = comunicado.tipo_icono || 'tramite';

    if (tipo === 'foto') {
      const src = comunicado.foto_url || cesarPhoto;
      return (
        <img
          src={src}
          alt="Comunicado Institucional"
          style={{
            width: '120px', height: '120px',
            borderRadius: '50%', objectFit: 'cover',
            border: '4px solid #1a1e38',
            boxShadow: '0 8px 18px rgba(0,0,0,0.18)'
          }}
        />
      );
    }

    let IconComp = FileText;
    if (tipo === 'institucional') IconComp = Landmark;
    if (tipo === 'ley') IconComp = Scale;

    return (
      <div style={{
        width: '96px', height: '96px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1a1e38 0%, #0f172a 100%)',
        border: '3px solid #d4af37',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto',
        boxShadow: '0 10px 25px -5px rgba(26, 30, 56, 0.4), 0 0 0 4px rgba(212, 175, 55, 0.15)'
      }}>
        <IconComp size={44} color="#d4af37" />
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '1.25rem'
    }}>
      <div className="animate-scale-in" style={{
        background: '#ffffff',
        borderRadius: '18px',
        maxWidth: '680px',
        width: '100%',
        maxHeight: '92vh',
        overflowY: 'auto',
        position: 'relative',
        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        {/* Botón de Cierre */}
        <button
          onClick={handleCerrar}
          aria-label="Cerrar modal"
          style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: '#f1f5f9', border: 'none',
            borderRadius: '50%', width: '36px', height: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', zIndex: 10,
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#e2e8f0';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <X size={18} color="#475569" />
        </button>

        {/* Badge indicador de Preview si está en modo admin */}
        {isPreview && (
          <div style={{
            position: 'absolute', top: '1.1rem', left: '1.25rem',
            background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
            fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: '4px',
            textTransform: 'uppercase', letterSpacing: '0.5px'
          }}>
            Vista Previa de Administrador
          </div>
        )}

        <div style={{ padding: '2.5rem 2rem 2rem 2rem' }}>
          {/* Cabecera / Ícono */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem', marginTop: isPreview ? '0.75rem' : '0' }}>
            {renderIcono()}
          </div>

          {/* Título Principal */}
          <h3 style={{
            fontSize: '1.55rem', color: '#0f172a',
            textAlign: 'center', marginBottom: comunicado.subtitulo ? '0.4rem' : '1.25rem',
            fontFamily: 'Space Grotesk, sans-serif', fontWeight: '800', lineHeight: 1.25
          }}>
            {comunicado.titulo}
          </h3>

          {/* Subtítulo si existe */}
          {comunicado.subtitulo && (
            <p style={{
              fontSize: '0.92rem', color: '#64748b', fontWeight: 600,
              textAlign: 'center', marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.4px'
            }}>
              {comunicado.subtitulo}
            </p>
          )}

          {/* Cuerpo del Comunicado */}
          <div style={{ color: '#334155', fontSize: '1.02rem', lineHeight: '1.75', textAlign: 'justify' }}>
            {comunicado.parrafo_1 && (
              <p style={{ marginBottom: '1.15rem' }}>
                {comunicado.parrafo_1}
              </p>
            )}

            {comunicado.parrafo_2 && (
              <p style={{ marginBottom: '1.15rem' }}>
                {comunicado.parrafo_2}
              </p>
            )}

            {/* Cuadro Destacado con Barra Dorada (Estilo Foto 2) */}
            {comunicado.destacado && (
              <div style={{
                marginTop: '1.35rem',
                padding: '1.25rem 1.4rem',
                backgroundColor: '#1a1e38',
                borderLeft: '4px solid #d4af37',
                borderRadius: '0 10px 10px 0',
                color: '#ffffff',
                fontSize: '0.95rem',
                lineHeight: '1.65',
                boxShadow: '0 4px 14px rgba(26, 30, 56, 0.2)'
              }}>
                {comunicado.destacado}
              </div>
            )}
          </div>

          {/* Barra de Acciones */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '2.25rem',
            flexWrap: 'wrap'
          }}>
            {comunicado.boton_texto && (
              <button
                onClick={handleBotonPrincipal}
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '0.98rem',
                  fontWeight: 700,
                  borderRadius: '10px',
                  backgroundColor: '#1a1e38',
                  color: '#ffffff',
                  border: '1px solid #d4af37',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(26, 30, 56, 0.25)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f172a';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#1a1e38';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span>{comunicado.boton_texto}</span>
                <ArrowRight size={16} color="#d4af37" />
              </button>
            )}

            {comunicado.mostrar_boton_secundario !== false && (
              <button
                onClick={handleCerrar}
                style={{
                  padding: '0.75rem 1.75rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '10px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#e2e8f0';
                  e.currentTarget.style.color = '#1e293b';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                {comunicado.boton_secundario_texto || 'Continuar al Sitio Web'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
